import { Car, Lightbulb, DoorOpen, Siren, Droplet, House, MapPin, PhoneOff, ArrowDown, Smartphone, LogIn, EyeOff } from "lucide-react";
import { SectionHeading } from "../common/SectionHeading";

const WHY = [
  { id: "blocking", icon: Car, t: "Blocking alert", b: "Someone needs you to move your vehicle. They can reach you without your phone number being publicly displayed." },
  { id: "lights", icon: Lightbulb, t: "Lights left on", b: "Someone notices your lights are still on. They can alert you before your battery dies." },
  { id: "door", icon: DoorOpen, t: "Door or window open", b: "Someone notices your vehicle is unsecured. They can let you know immediately." },
  { id: "accident", icon: Siren, t: "Accident", b: "An accident happens around your vehicle. CARLINK provides a way to reach the owner and trigger emergency communication.", em: true },
  { id: "blood", icon: Droplet, t: "Blood required", b: "Someone connected to the vehicle may urgently need assistance. Emergency communication can be connected to configured contacts.", em: true },
  { id: "family", icon: House, t: "Inform family", b: "In an unexpected emergency, the right people may need to know quickly. Your configured emergency contacts can become part of the communication chain.", em: true },
  { id: "help", icon: MapPin, t: "Need help", b: "Someone may need to communicate something important about the vehicle or its location." },
  { id: "searching", icon: PhoneOff, t: "No more searching", b: "No searching for the owner. No waiting for security. No random notes under the windshield. Just scan CARLINK." },
];

export const WhyCarlinkSection = () => (
  <section id="why-carlink" data-testid="why-carlink-section" className="section-pad border-t border-white/5">
    <div className="container-x">
      <SectionHeading label="Why CARLINK?" title="It isn't just a QR sticker." body="It's a communication layer for your vehicle when you're not there." />
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => (
          <article key={w.id} data-testid={`why-card-${w.id}`} style={{ animationDelay: `${i * 60}ms` }}
            className={`card-premium group reveal p-6 ${w.em ? "!border-red-500/30" : ""}`}>
            <w.icon className={`h-6 w-6 transition-transform duration-300 group-hover:-translate-y-0.5 ${w.em ? "text-red-400" : "text-[#FF5500]"}`} aria-hidden="true" />
            <h3 className="mt-6 font-display text-lg font-semibold uppercase tracking-tight text-slate-50">{w.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.b}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const STORIES = [
  ["You park your vehicle and walk away.", "Someone notices your lights are ON."],
  ["You accidentally block another vehicle.", "They scan CARLINK."],
  ["Someone notices your door is open.", "They scan CARLINK."],
  ["An accident happens.", "Someone scans CARLINK. They can contact the owner and trigger the emergency workflow."],
  ["Someone needs to inform your family.", "Your configured emergency contacts can become part of the communication chain."],
];

export const ImagineSection = () => (
  <section data-testid="imagine-section" className="section-pad border-t border-white/5 bg-[#0B0D14]">
    <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.4fr]">
      <div className="lg:sticky lg:top-28 lg:self-start">
        <p className="label-mono text-[#FF5500]">Imagine this</p>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-slate-50 sm:text-5xl">CARLINK is there when you aren't.</h2>
      </div>
      <ol className="space-y-4">
        {STORIES.map(([a, b], i) => (
          <li key={a} data-testid={`imagine-story-${i}`} className="card-premium flex gap-5 p-6">
            <span className="font-mono text-sm font-bold text-[#FF5500]">0{i + 1}</span>
            <div><p className="font-display text-lg font-semibold text-slate-50">{a}</p><p className="mt-1 text-sm text-slate-400">{b}{i < 3 && " You get notified."}</p></div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const CHAIN = ["Vehicle", "Person nearby", "CARLINK", "Owner", "Emergency contacts"];

export const NetworkSection = () => (
  <section data-testid="network-section" className="section-pad relative overflow-hidden border-t border-white/5">
    <div className="grid-bg absolute inset-0 opacity-40" aria-hidden="true" />
    <div className="container-x relative grid items-center gap-12 lg:grid-cols-2">
      <div>
        <p className="label-mono text-[#FF5500]">The CARLINK network</p>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-slate-50 sm:text-5xl">One small sticker.<br /><span className="text-[#FF5500]">A big communication network.</span></h2>
        <p className="mt-6 max-w-md text-base text-slate-400">Without publicly displaying your personal phone number.</p>
      </div>
      <ol data-testid="network-chain" className="mx-auto w-full max-w-xs">
        {CHAIN.map((c, i) => (
          <li key={c} className="flex flex-col items-center">
            <span className={`w-full rounded-xl border px-5 py-4 text-center font-display text-sm font-bold uppercase tracking-widest ${c === "CARLINK" ? "border-[#FF5500] bg-[#FF5500] text-white" : "border-white/10 bg-[#0F111A] text-slate-100"}`}>{c}</span>
            {i < CHAIN.length - 1 && <ArrowDown className="my-2 h-4 w-4 text-[#FF5500]" aria-hidden="true" />}
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const MoreThanParkingSection = () => (
  <section data-testid="more-than-parking-section" className="section-pad border-t border-white/5 bg-[#0B0D14]">
    <div className="container-x grid gap-12 lg:grid-cols-2">
      <div>
        <p className="label-mono text-red-400">Emergency contacts</p>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-slate-50 sm:text-5xl">CARLINK is more than a parking QR.</h2>
        <p className="mt-6 max-w-md text-base text-slate-400">Because sometimes the person who needs to reach you isn't trying to complain. They may be trying to help.</p>
      </div>
      <ol className="space-y-3">
        {["Someone finds your vehicle after an accident.", "They scan CARLINK.", "They know who the vehicle belongs to.", "They can contact the owner.", "They can trigger an emergency workflow.", "Your trusted emergency contacts can be part of the communication chain."].map((t, i) => (
          <li key={t} data-testid={`parking-step-${i}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0F111A] px-5 py-4 text-slate-100"><span className="font-mono text-sm font-bold text-red-400">0{i + 1}</span>{t}</li>
        ))}
        <li className="pt-2 font-display text-lg font-bold uppercase text-[#FF5500]">That is the value of CARLINK.</li>
      </ol>
    </div>
  </section>
);

export const WhenNotThereSection = () => (
  <section data-testid="when-not-there-section" className="section-pad border-t border-white/5">
    <div className="container-x">
      <SectionHeading label="Everyday & unexpected" title={<>When you're not there,<br />CARLINK helps people reach you.</>} />
      <div className="mt-12 grid gap-4 md:grid-cols-2">
        {[["For everyday situations", ["Blocking", "Lights", "Door / window", "Vehicle issue"], false], ["For unexpected situations", ["Accident", "Medical emergency", "Blood requirement", "Family notification", "Location sharing"], true]].map(([t, items, em]) => (
          <div key={t} className={`card-premium p-7 ${em ? "!border-red-500/30" : ""}`}>
            <p className={`label-mono ${em ? "text-red-400" : "text-[#FF5500]"}`}>{t}</p>
            <ul className="mt-5 flex flex-wrap gap-2">{items.map((i) => <li key={i} className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200">{i}</li>)}</ul>
          </div>
        ))}
      </div>
      <p className="mt-10 max-w-2xl text-lg text-slate-300">CARLINK creates a communication bridge between your vehicle and the people around it.</p>
    </div>
  </section>
);

export const NoAppSection = () => (
  <section data-testid="no-app-section" className="border-t border-white/5 bg-[#FF5500] py-20 text-[#07080C] sm:py-28">
    <div className="container-x">
      <div className="grid gap-6 sm:grid-cols-3">
        {[[Smartphone, "No app."], [LogIn, "No login."], [EyeOff, "No phone number exposed."]].map(([I, t]) => (
          <p key={t} className="flex items-center gap-3 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl"><I className="h-8 w-8 shrink-0" aria-hidden="true" />{t}</p>
        ))}
      </div>
      <p className="mt-10 max-w-xl text-lg font-semibold">Just scan the CARLINK sticker.</p>
      <p className="mt-2 font-mono text-sm font-bold uppercase tracking-[0.3em]">Report · Contact · Connect</p>
    </div>
  </section>
);
